import { _WINDOW_ } from "constants/dom";
import { TRUE, FALSE, NULL } from "constants/primitives";
import isTCFCategoryAccepted from "helpers/tcf/tcfHelpers/isTCFCategoryAccepted";
import useAppState from "hooks/useAppState";
import { useReducer, useEffect, useRef, useState } from "preact/hooks";
import {
  createTCFConsentStateReducer,
  calculateTCFInitialState,
} from "./TCFconsentStateReducer";
import {
  TCF_CONSENT_STATE_ACTION_CONSENT_SAVE_FINISHED,
  TCF_CONSENT_STATE_ACTION_START_CONSENT_SAVE,
  TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY,
  TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR,
  TCFConsentStateToggleCategoryAction,
  TCFConsentStateToggleVendorAction,
  ITCFConsentStateInput,
  TCF_CONSENT_STATE_ACTION_ACCEPT_ALL,
  TCFConsentStateAcceptAll,
  TCFConsentStateReportStartConsentSaveAction,
  TCFConsentStateReportConsentSaveFinishedAction,
  TCF_CONSENT_STATE_ACTION_LOG_TO_API,
  TCFConsentStateLogToAPI,
  TCFConsentStateDeny,
  TCF_CONSENT_STATE_ACTION_DENY,
  TCF_CONSENT_STATE_ACTION_TOGGLE_GATP_VENDOR,
  TCFConsentStateToggleGatpVendorAction,
  TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR_LEGITIMATE_INTEREST,
  TCFConsentStateToggleVendorLegitimateInterestAction,
  TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY_LEGITIMATE_INTEREST,
  TCFConsentStateToggleCategoryLegitimateInterestAction,
  TCFConsentStateAcceptAllLegitimateInterest,
  TCF_CONSENT_STATE_ACTION_ACCEPT_ALL_LEGITIMATE_INTEREST,
  ITCFVendorCategory,
  ITCFConsent,
  TCF_CONSENT_STATE_ACTION_SET_CONSENT,
  TCFConsentStateSetConsent,
  TCFVendorTypes,
} from "./types";
import createPromise from "helpers/createPromise";
import { ITCFConsentContext } from "./TCFUserConsentContext";
import { ITCFGatpVendor, ITCFGlobalVendorList, ITCFVendor } from "types/tcf";
import isTCFVendorAccepted from "../tcfHelpers/isTCFVendorAccepted";
import { EVENTS_PREFIX } from "core/config";
import useTranslation from "hooks/useTranslation";
import useUserConsent from "hooks/useUserConsent";
import isTCFCategoryLegitimateInterestAccepted from "../tcfHelpers/isTCFCategoryLegitimateInterestAccepted";
import shouldUseFullConsent from "helpers/consent/shouldUseFullConsent";
import { dispatchTCFAction } from "./dispatchTCFAction";

const useTCFManageConsentState = (
  input: Omit<ITCFConsentStateInput, "tcfConfig">
): TCFConsentStateManagerReturn => {
  const { userLang } = useTranslation();
  const {
    widgetConfig,
    gvl,
    gatp,
    visitorCountry,
    visitorRegion,
    setShouldTriggerTCFConsentEvent,
    tcfVendorsList
  } = useAppState();
  const { setVisitorId } = useUserConsent();
  const [triggerTCFConsentUpdate, setTriggerTCFConsentUpdate] = useState(FALSE);
  const [state, dispatch] = useReducer(
    createTCFConsentStateReducer(),
    calculateTCFInitialState({ ...input })
  );
  const initLegitimateInterestRef = useRef(FALSE);
  // This is being used to determinate moment of event dispatch, so other scripts, mostly TCF API can make re-call of all events that registered before the api was initialized
  const shouldBeSaved = useRef(FALSE);
  const saveInProgress = useRef(FALSE);

  useEffect(() => {
    if (gvl) {
      /**
       * Accept all vendors available for tcf
       */
      const acceptAll = (): void => {
        // Dispatch action to accept all
        dispatchTCFAction<TCFConsentStateAcceptAll>(
          TCF_CONSENT_STATE_ACTION_ACCEPT_ALL,
          { gvl, tcfVendorsList },
          dispatch
        );
      };

      // Do it only once, after gvl has been loaded
      if (!initLegitimateInterestRef.current) {
        initLegitimateInterestRef.current = TRUE;

        // Check if user already consented
        if (!state.hasConsented) {
          if (
            shouldUseFullConsent(widgetConfig, visitorCountry, visitorRegion)
          ) {
            acceptAll();

            // Dispatch action to set that consent has been saved, but do that without actually saving to local consent nor database
            dispatchTCFAction<TCFConsentStateReportStartConsentSaveAction>(
              TCF_CONSENT_STATE_ACTION_START_CONSENT_SAVE,
              { gvl, userLang, widgetConfig, forceFakeSave: true, tcfVendorsList },
              dispatch,
            );

            // Toggle information that at next state change, action to save should be triggered.
            shouldBeSaved.current = TRUE;
          } else {
            const acceptAllLegitimateInterest = (): void => {
              // Dispatch action to accept all
              dispatchTCFAction<TCFConsentStateAcceptAllLegitimateInterest>(
                TCF_CONSENT_STATE_ACTION_ACCEPT_ALL_LEGITIMATE_INTEREST,
                { gvl, tcfVendorsList },
                dispatch
              );
            };

            acceptAllLegitimateInterest();
          }
        }
      }

      // Check if should wait for gatp or not
      if (widgetConfig.acEnabled) {
        if (!gatp) {
          return;
        }
      }

      const handleLogConsentEvent = (e: CustomEventInit) => {
        dispatchTCFAction<TCFConsentStateLogToAPI>(
          TCF_CONSENT_STATE_ACTION_LOG_TO_API,
          { gvl, consentInfo: e.detail, userLang, setVisitorId, widgetConfig, tcfVendorsList },
          dispatch
        );
      };

      // Read for event to log consent to api
      _WINDOW_.addEventListener(
        `${EVENTS_PREFIX}_tcf_log_consent_to_api`,
        handleLogConsentEvent
      );

      // At init of TCF state, init event listener for acceptALL
      _WINDOW_.addEventListener(
        `${EVENTS_PREFIX}_consent_accept_all`,
        acceptAll
      );

      /**
       * Deny all vendors available for tcf
       */
      const deny = (): void => {
        // Dispatch action to make all denyed
        dispatchTCFAction<TCFConsentStateDeny>(TCF_CONSENT_STATE_ACTION_DENY, {
          gvl,
          tcfVendorsList,
        }, dispatch);
      };

      // At init of TCF state, init event listener for "Deny" action
      _WINDOW_.addEventListener(`${EVENTS_PREFIX}_denied_action`, deny);

      const handleTCFConsent = (gvl: ITCFGlobalVendorList): Promise<void> => {
        return createPromise<void>(() => {
          dispatchTCFAction<TCFConsentStateReportStartConsentSaveAction>(
            TCF_CONSENT_STATE_ACTION_START_CONSENT_SAVE,
            { gvl, userLang, widgetConfig, tcfVendorsList },
            dispatch
          );

          // Toggle information that at next state change, action to save should be triggered.
          shouldBeSaved.current = TRUE;
        });
      };

      const handleConsentEvent = () => handleTCFConsent(gvl);

      // And for when user do action with consent
      _WINDOW_.addEventListener(
        `${EVENTS_PREFIX}_consent_action`,
        handleConsentEvent
      );

      return () => {
        _WINDOW_.removeEventListener(
          `${EVENTS_PREFIX}_consent_action`,
          handleConsentEvent
        );
        _WINDOW_.removeEventListener(
          `${EVENTS_PREFIX}_consent_accept_all`,
          acceptAll
        );
        _WINDOW_.removeEventListener(
          `${EVENTS_PREFIX}_tcf_log_consent_to_api`,
          handleLogConsentEvent
        );
        _WINDOW_.removeEventListener(`${EVENTS_PREFIX}_denied_action`, deny);
      };
    }
  }, [gvl, userLang, widgetConfig, gatp]);

  useEffect(() => {
    setTriggerTCFConsentUpdate(FALSE);

    // Check if process of saving was started, that means the next state update is the saved one, so we can notify api via event
    if (saveInProgress.current) {
      saveInProgress.current = FALSE; // Dispatch even that TCF consent has been updated
      setShouldTriggerTCFConsentEvent(TRUE);
    }

    // Check if process of saving should start
    if (shouldBeSaved.current) {
      shouldBeSaved.current = FALSE;
      saveInProgress.current = TRUE;

      dispatchTCFAction<TCFConsentStateReportConsentSaveFinishedAction>(
        TCF_CONSENT_STATE_ACTION_CONSENT_SAVE_FINISHED,
        NULL,
        dispatch
      );

      setTriggerTCFConsentUpdate(TRUE);
    }
  }, [state, setShouldTriggerTCFConsentEvent]);

  const isVendorToggleEnabled = (vendor: ITCFVendor) => {
    return isTCFVendorAccepted(vendor.id, state.tempConsent, TCFVendorTypes.VENDORS);
  };

  const isVendorLegitimateInterestToggleEnabled = (vendor: ITCFVendor) => {
    return isTCFVendorAccepted(vendor.id, state.tempConsent, TCFVendorTypes.LEG_INT);
  };

  const isGatpVendorToggleEnabled = (vendor: ITCFGatpVendor) => {
    return isTCFVendorAccepted(vendor.provider_id, state.tempConsent, TCFVendorTypes.GATP);
  };

  const isCategoryToggleEnabled = (name: ITCFVendorCategory, id: number) => {
    return isTCFCategoryAccepted(name, id, state.tempConsent);
  };

  const isCategoryLegitimateInterestToggleEnabled = (
    name: ITCFVendorCategory,
    id: number
  ) => {
    return isTCFCategoryLegitimateInterestAccepted(name, id, state.tempConsent);
  };

  const setConsent = (consent: ITCFConsent) => {
    dispatchTCFAction<TCFConsentStateSetConsent>(
      TCF_CONSENT_STATE_ACTION_SET_CONSENT,
      { consent },
      dispatch
    );
  };

  const toggleVendor = (vendor: ITCFVendor, gvl: ITCFGlobalVendorList) => {
    setVendor(vendor, !isVendorToggleEnabled(vendor), gvl);
  };

  const setVendor = (
    vendor: ITCFVendor,
    value: boolean,
    gvl: ITCFGlobalVendorList
  ) => {
    dispatchTCFAction<TCFConsentStateToggleVendorAction>(
      TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR,
      { vendorID: vendor.id, shouldBeAccepted: value, gvl, widgetConfig },
      dispatch
    );
  };

  const toggleVendorLegitimateInterest = (
    vendor: ITCFVendor,
    gvl: ITCFGlobalVendorList
  ) => {
    setVendorLegitimateInterest(
      vendor,
      !isVendorLegitimateInterestToggleEnabled(vendor),
      gvl
    );
  };

  const setVendorLegitimateInterest = (
    vendor: ITCFVendor,
    value: boolean,
    gvl: ITCFGlobalVendorList
  ) => {
    dispatchTCFAction<TCFConsentStateToggleVendorLegitimateInterestAction>(
      TCF_CONSENT_STATE_ACTION_TOGGLE_VENDOR_LEGITIMATE_INTEREST,
      { vendorID: vendor.id, shouldBeAccepted: value, gvl, widgetConfig },
      dispatch
    );
  };

  const toggleGatpVendor = (vendor: ITCFGatpVendor) => {
    setGatpVendor(vendor, !isGatpVendorToggleEnabled(vendor));
  };

  const setGatpVendor = (vendor: ITCFGatpVendor, value: boolean) => {
    dispatchTCFAction<TCFConsentStateToggleGatpVendorAction>(
      TCF_CONSENT_STATE_ACTION_TOGGLE_GATP_VENDOR,
      { vendorID: vendor.provider_id, shouldBeAccepted: value },
      dispatch
    );
  };

  const toggleCategory = (
    name: ITCFVendorCategory,
    id: number,
    gvl: ITCFGlobalVendorList
  ) => {
    setCategoryValue(name, id, !isCategoryToggleEnabled(name, id), gvl);
  };

  const setCategoryValue = (
    name: ITCFVendorCategory,
    id: number,
    value: boolean,
    gvl: ITCFGlobalVendorList
  ) => {
    dispatchTCFAction<TCFConsentStateToggleCategoryAction>(
      TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY,
      { name, id, gvl, shouldBeAccepted: value, tcfVendorsList },
      dispatch
    );
  };

  const toggleCategoryLegitimateInterest = (
    name: ITCFVendorCategory,
    id: number,
    gvl: ITCFGlobalVendorList
  ) => {
    setCategoryLegitimateInterestValue(
      name,
      id,
      !isCategoryLegitimateInterestToggleEnabled(name, id),
      gvl
    );
  };

  const setCategoryLegitimateInterestValue = (
    name: ITCFVendorCategory,
    id: number,
    value: boolean,
    gvl: ITCFGlobalVendorList
  ) => {
    dispatchTCFAction<TCFConsentStateToggleCategoryLegitimateInterestAction>(
      TCF_CONSENT_STATE_ACTION_TOGGLE_CATEGORY_LEGITIMATE_INTEREST,
      { name, id, gvl, shouldBeAccepted: value },
      dispatch
    );
  };

  return [
    {
      ...state,
      triggerTCFConsentUpdate,
      // functions modifying temp state
      setConsent,
      toggleVendor,
      setVendor,
      toggleVendorLegitimateInterest,
      setVendorLegitimateInterest,
      toggleCategory,
      setCategoryValue,
      toggleCategoryLegitimateInterest,
      setCategoryLegitimateInterestValue,
      toggleGatpVendor,
      setGatpVendor,
      // consent querying functions
      isGatpVendorAccepted: isGatpVendorToggleEnabled,
      isVendorAccepted: isVendorToggleEnabled,
      isVendorLegitimateInterestAccepted:
        isVendorLegitimateInterestToggleEnabled,
      isCategoryAccepted: isCategoryToggleEnabled,
      isCategoryLegitimateInterestAccepted:
        isCategoryLegitimateInterestToggleEnabled,
    },
  ];
};

export type TCFConsentStateManagerReturn = [ITCFConsentContext];

export default useTCFManageConsentState;
